import React, { useState, useEffect } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import DailySchedule from './components/DailySchedule';
import InterventionDetails from './components/InterventionDetails';
import CompletionModal from './components/CompletionModal';
import { useNavigate } from 'react-router-dom';
import { smsNotificationService } from '../../services/smsNotificationService';

const TechnicianDailyRoutes = () => {
  const navigate = useNavigate();
  const [activeAppointment, setActiveAppointment] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [appointments, setAppointments] = useState([
  {
    id: 1,
    customerName: 'Marie Dubois',
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1479bf0bf-1763297239550.png",
    customerAvatarAlt: 'Photo professionnelle de Marie Dubois, femme aux cheveux bruns courts portant un chemisier blanc',
    serviceType: 'Réparation fuite robinet cuisine',
    scheduledTime: new Date(2025, 10, 30, 9, 0),
    estimatedDuration: '1h30',
    address: '15 Rue de la République, 75001 Paris',
    phone: '+33 6 12 34 56 78',
    urgency: 'urgent',
    status: 'planned',
    notes: 'Fuite importante sous l\'évier. Client disponible toute la matinée.',
    latitude: 48.8566,
    longitude: 2.3522
  },
  {
    id: 2,
    customerName: 'Jean Martin',
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1239b4cc9-1763296026764.png",
    customerAvatarAlt: 'Photo professionnelle de Jean Martin, homme aux cheveux gris portant une chemise bleue',
    serviceType: 'Installation chauffe-eau',
    scheduledTime: new Date(2025, 10, 30, 11, 0),
    estimatedDuration: '2h00',
    address: '28 Avenue des Champs-Élysées, 75008 Paris',
    phone: '+33 6 23 45 67 89',
    urgency: 'today',
    status: 'planned',
    notes: 'Nouveau chauffe-eau électrique 200L. Accès facile au local technique.',
    latitude: 48.8698,
    longitude: 2.3078
  },
  {
    id: 3,
    customerName: 'Sophie Laurent',
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15fea54ff-1763300851576.png",
    customerAvatarAlt: 'Photo professionnelle de Sophie Laurent, femme aux cheveux blonds longs portant un pull beige',
    serviceType: 'Débouchage canalisation',
    scheduledTime: new Date(2025, 10, 30, 14, 30),
    estimatedDuration: '1h00',
    address: '42 Boulevard Saint-Germain, 75005 Paris',
    phone: '+33 6 34 56 78 90',
    urgency: 'scheduled',
    status: 'planned',
    notes: 'Problème récurrent. Vérifier l\'état général des canalisations.',
    latitude: 48.8534,
    longitude: 2.3488
  },
  {
    id: 4,
    customerName: 'Pierre Rousseau',
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1942731b4-1763296933880.png",
    customerAvatarAlt: 'Photo professionnelle de Pierre Rousseau, homme jeune aux cheveux noirs courts portant une veste grise',
    serviceType: 'Remplacement joints salle de bain',
    scheduledTime: new Date(2025, 10, 30, 16, 0),
    estimatedDuration: '1h30',
    address: '8 Rue du Faubourg Saint-Honoré, 75008 Paris',
    phone: '+33 6 45 67 89 01',
    urgency: 'scheduled',
    status: 'planned',
    notes: 'Joints de douche et lavabo à remplacer. Matériel fourni par le client.',
    latitude: 48.8704,
    longitude: 2.3167
  }]
  );

  const handleSelectAppointment = (appointment) => {
    setActiveAppointment(appointment);
    if (appointment?.status === 'planned') {
      setAppointments((prev) =>
      prev?.map((a) =>
      a?.id === appointment?.id ?
      { ...a, status: 'on_site', startTime: new Date()?.toISOString() } :
      a
      )
      );
    }
  };

  const handleStatusUpdate = async (appointment) => {
    const statusFlow = {
      planned: 'en_route',
      en_route: 'on_site',
      on_site: 'on_site'
    };

    const newStatus = statusFlow?.[appointment?.status];
    if (newStatus) {
      setAppointments((prev) =>
      prev?.map((a) =>
      a?.id === appointment?.id ?
      { ...a, status: newStatus, startTime: newStatus === 'on_site' ? new Date()?.toISOString() : a?.startTime } :
      a
      )
      );
      if (newStatus === 'on_site') {
        setActiveAppointment({ ...appointment, status: newStatus, startTime: new Date()?.toISOString() });
        
        // Send SMS notification when technician arrives
        try {
          const appointmentData = {
            reference: appointment?.reference || `APT-${appointment?.id}`,
            customerName: appointment?.customerName,
            customerEmail: appointment?.customerEmail || '',
            customerPhone: appointment?.phone,
            serviceType: appointment?.serviceType,
            scheduledDate: appointment?.scheduledTime?.toLocaleDateString('fr-FR'),
            customerAddress: appointment?.address
          };

          await smsNotificationService?.sendWithPreferenceCheck(
            appointmentData,
            'arrival'
          );
        } catch (smsError) {
          console.error('SMS notification failed:', smsError);
          // Don't block the flow if SMS fails
        }
      }
      
      // Send SMS when technician is en route
      if (newStatus === 'en_route') {
        try {
          const appointmentData = {
            reference: appointment?.reference || `APT-${appointment?.id}`,
            customerName: appointment?.customerName,
            customerEmail: appointment?.customerEmail || '',
            customerPhone: appointment?.phone,
            serviceType: appointment?.serviceType,
            scheduledDate: appointment?.scheduledTime?.toLocaleDateString('fr-FR'),
            customerAddress: appointment?.address,
            technicianName: 'Marc Lefebvre',
            estimatedArrivalTime: new Date(Date.now() + 20 * 60000)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
          };

          await smsNotificationService?.sendWithPreferenceCheck(
            appointmentData,
            'technician_en_route'
          );
        } catch (smsError) {
          console.error('SMS notification failed:', smsError);
          // Don't block the flow if SMS fails
        }
      }
    }
  };

  const handleNavigate = (appointment) => {
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${appointment?.latitude},${appointment?.longitude}`;
    window.open(mapsUrl, '_blank');
  };

  const handleCall = (appointment) => {
    window.location.href = `tel:${appointment?.phone}`;
  };

  const handleComplete = () => {
    setShowCompletionModal(true);
  };

  const handleConfirmCompletion = (completionData) => {
    setAppointments((prev) =>
    prev?.map((a) =>
    a?.id === completionData?.appointmentId ?
    { ...a, status: 'completed', completedAt: completionData?.completedAt } :
    a
    )
    );
    setShowCompletionModal(false);
    setActiveAppointment(null);
  };

  const handleUpdatePhotos = (photos) => {
    setActiveAppointment((prev) => ({
      ...prev,
      photos: photos
    }));
  };

  const handleUpdateSignature = (signature) => {
    setActiveAppointment((prev) => ({
      ...prev,
      signature: signature
    }));
  };

  const handleLogout = () => {
    navigate('/login-authentication');
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:+33123456789';
  };

  const handleQuickBooking = () => {
    navigate('/service-booking-wizard');
  };

  const handleSupportChat = () => {
    console.log('Opening support chat...');
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole="technician"
        userName="Marc Lefebvre"
        onLogout={handleLogout} />


      <main className="main-content">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-120px)]">
            <div className="h-full">
              <DailySchedule
                appointments={appointments}
                activeAppointment={activeAppointment}
                onSelectAppointment={handleSelectAppointment}
                onStatusUpdate={handleStatusUpdate}
                onNavigate={handleNavigate}
                onCall={handleCall} />

            </div>

            <div className="h-full">
              {activeAppointment ?
              <InterventionDetails
                appointment={activeAppointment}
                onComplete={handleComplete}
                onUpdatePhotos={handleUpdatePhotos}
                onUpdateSignature={handleUpdateSignature} /> :


              <div className="bg-card rounded-lg shadow-sm h-full flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Sélectionnez une intervention
                    </h3>
                    <p className="text-muted-foreground max-w-sm mx-auto">
                      Choisissez une intervention dans votre planning pour commencer le suivi et la documentation
                    </p>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </main>

      <QuickActionToolbar
        userRole="technician"
        onEmergencyCall={handleEmergencyCall}
        onQuickBooking={handleQuickBooking}
        onSupportChat={handleSupportChat} />


      {showCompletionModal &&
      <CompletionModal
        isOpen={showCompletionModal}
        appointment={activeAppointment}
        onClose={() => setShowCompletionModal(false)}
        onConfirm={handleConfirmCompletion}
        onStatusUpdate={handleStatusUpdate} />
      }
    </div>);

};

export default TechnicianDailyRoutes;